import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { Button, Checkbox, Form, FormInstance, Input, Radio, Tooltip, Typography, notification } from 'antd'

import { txt } from '../../../locales/i18n'
import * as Municipalities from '../../../context/municipalities'
import * as MunicipalityService from '../../../services/bb-api/municipality'

export type MunicipalitySelectorProps = {
	form: FormInstance
	label: string
	name: string[]
	onChange: () => void
}
type MunicipalityCheckbox = { label: string; value: number }

const flagSources = {
	dk: 'https://filer.boblberg.dk/Assets/Img/flags/da.png',
	se: 'https://filer.boblberg.dk/Assets/Img/flags/sv.png',
	de: 'https://filer.boblberg.dk/Assets/Img/flags/de.png',
} as const

export const MunicipalitySelector = (props: MunicipalitySelectorProps) => {
	const [loading, setLoading] = useState(true)
	const [municipalitySearch, setMunicipalitySearch] = useState('')
	const [municipalities, setMunicipalities] = useState<MunicipalityCheckbox[]>([])
	const [country, setCountry] = useState<string | null>(null)
	const [status, setStatus] = useState<string | null>(null)

	useEffect(() => {
		setCountry(props.form.getFieldValue([...props.name, 'country']) ?? null)
		setStatus(props.form.getFieldValue([...props.name, 'status']) ?? null)
	}, [props.name, props.form, country, status])

	useEffect(() => {
		if (country === null || status === null) return

		const getMunicipalities = async () => {
			setLoading(true)
			const municipalityIdsSelected: number[] = props.form.getFieldValue([...props.name, 'values']) ?? []

			const query: { country: string; active?: boolean } = { country }
			if (status !== 'all') query.active = status === 'active'

			try {
				type Municipality = { id: number; name: string }
				const municipalities: Municipality[] = await MunicipalityService.query(query)

				const municipalityIdsSelectedFiltered = municipalityIdsSelected.filter((id: number) => {
					return municipalities.some((municipality) => municipality.id === id)
				})

				if (municipalityIdsSelected.length !== 0) {
					props.form.setFields([{ name: [...props.name, 'values'], value: municipalityIdsSelectedFiltered }])
				}

				const municipalityCheckboxValues = municipalities.map(({ id, name }) => ({ value: id, label: name }))
				setMunicipalities(municipalityCheckboxValues)
			} catch (error) {
				const message = txt('notification.unknown error')
				notification.error({ message })
			}

			setLoading(false)
		}

		getMunicipalities()
	}, [country, status, props.form, props.name])

	const selectMunicipalities = (type: string) => {
		props.onChange()

		if (type === 'all') {
			const municipalityIds = municipalities.map(({ value }) => value)
			return props.form.setFields([{ name: [...props.name, 'values'], value: municipalityIds }])
		}

		if (type === 'none') {
			return props.form.setFields([{ name: [...props.name, 'values'], value: [] }])
		}

		const region = Municipalities.regions[type] ?? null
		if (region === null) return

		const regionFiltered = region.filter((municipalityId) => {
			return municipalities.some(({ value }) => value === municipalityId)
		})

		const currentSelections = props.form.getFieldValue([...props.name, 'values'])
		const value = [...currentSelections, ...regionFiltered]
		return props.form.setFields([{ name: [...props.name, 'values'], value }])
	}

	return (
		<MunicipalitySelectorStyled>
			<label>{props.label}</label>

			<div>
				<div className="horizontal gap wrap aligned-center justified-center">
					<div>
						<Tooltip title={txt('global.country')}>
							<span>
								<Form.Item noStyle initialValue="Denmark" name={[...props.name, 'country']}>
									<Radio.Group onChange={({ target }) => setCountry(target.value)}>
										<Radio value="Denmark">
											<img className="flag" src={flagSources.dk} alt="dk flag" />
										</Radio>

										<Radio value="Sweden">
											<img className="flag" src={flagSources.se} alt="se flag" />
										</Radio>

										<Radio value="Germany">
											<img className="flag" src={flagSources.de} alt="de flag" />
										</Radio>
									</Radio.Group>
								</Form.Item>
							</span>
						</Tooltip>
					</div>

					<div>
						<Tooltip title={txt('global.status')}>
							<span>
								<Form.Item noStyle initialValue="all" name={[...props.name, 'status']}>
									<Radio.Group
										className="align-self-center"
										onChange={({ target }) => setStatus(target.value)}
									>
										<Radio value="all">{txt('global.all')}</Radio>
										<Radio value="active">{txt('global.active')}</Radio>
										<Radio value="inactive">{txt('global.inactive')}</Radio>
									</Radio.Group>
								</Form.Item>
							</span>
						</Tooltip>
					</div>
				</div>
			</div>

			<Input
				allowClear
				placeholder={txt('global.search')}
				onChange={({ target }) => setMunicipalitySearch(target.value)}
			/>

			<Form.Item name={[...props.name, 'values']}>
				<Checkbox.Group disabled={loading}>
					{municipalities.map((municipality) => {
						const searchNormalized = municipalitySearch.toLowerCase()
						const labelNormalized = municipality.label.toLowerCase()

						return (
							<Checkbox
								key={municipality.value}
								value={municipality.value}
								style={{ display: labelNormalized.startsWith(searchNormalized) ? 'flex' : 'none' }}
							>
								<Tooltip title={municipality.label}>
									<Typography.Text ellipsis>{municipality.label}</Typography.Text>
								</Tooltip>
							</Checkbox>
						)
					})}
				</Checkbox.Group>
			</Form.Item>

			{municipalitySearch === '' && (
				<div className="horizontal wrap gap justified-center">
					<Button danger size="small" type="link" onClick={() => selectMunicipalities('none')}>
						{txt('municipality-buttons.none')}
					</Button>

					<Button size="small" type="link" onClick={() => selectMunicipalities('all')}>
						{txt('municipality-buttons.all')}
					</Button>

					{country === 'Denmark' && (
						<>
							<Button size="small" type="link" onClick={() => selectMunicipalities('Fyn')}>
								{txt('municipality-buttons.fyn')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Nordjylland')}>
								{txt('municipality-buttons.north-jytland-short')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Midt-Vestjylland')}>
								{txt('municipality-buttons.mid-west-jytland-short')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Midt-Østjylland')}>
								{txt('municipality-buttons.mid-east-jytland-short')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Sønderjylland')}>
								{txt('municipality-buttons.south-jytland-short')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Storkøbenhavn')}>
								{txt('municipality-buttons.copenhagen-short')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Vestegnen')}>
								{txt('municipality-buttons.west-copenhagen')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Nordsjælland')}>
								{txt('municipality-buttons.north-zealand-short')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Vestsjælland')}>
								{txt('municipality-buttons.west-zealand-short')}
							</Button>

							<Button size="small" type="link" onClick={() => selectMunicipalities('Sydøstsjælland')}>
								{txt('municipality-buttons.south-east-zealand-short')}
							</Button>
						</>
					)}
				</div>
			)}
		</MunicipalitySelectorStyled>
	)
}

const MunicipalitySelectorStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.ant-checkbox-group {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(92px, 1fr));
		gap: 1ch 2ch;
		max-height: 400px;
		overflow-y: auto;

		.ant-checkbox-wrapper,
		.ant-typography {
			margin: 0;
			font-weight: normal;
		}

		.ant-checkbox-wrapper {
			span:last-child {
				overflow-x: hidden;
			}
		}
	}
`
